/**
 * API Error Handler & Validation
 *
 * Provides consistent error handling across all API calls
 * and validates request/response data
 */

import { forceSignOut, signOut } from './supabase'

/**
 * Standardized API error response format
 */
export interface ApiError {
  code: string
  message: string
  details?: Record<string, unknown>
  retryable: boolean
}

/**
 * Parse API error response and return standardized format
 */
export async function parseApiError(response: Response): Promise<ApiError> {
  try {
    const data = await response.json()

    // Map common HTTP status codes to error codes
    const statusToCode: Record<number, string> = {
      400: 'VALIDATION_ERROR',
      401: 'UNAUTHORIZED',
      403: 'FORBIDDEN',
      404: 'NOT_FOUND',
      409: 'CONFLICT',
      500: 'SERVER_ERROR',
      503: 'SERVICE_UNAVAILABLE',
    }

    const code = statusToCode[response.status] || 'API_ERROR'
    const message = data.error || response.statusText || 'Unknown error'

    return {
      code,
      message,
      details: data.details,
      retryable: response.status >= 500 || response.status === 429,
    }
  } catch {
    // Fallback if response is not JSON
    return {
      code: 'NETWORK_ERROR',
      message: `HTTP ${response.status}: ${response.statusText}`,
      retryable: response.status >= 500,
    }
  }
}

/**
 * Validate API key format before sending
 */
export function validateApiKey(key: string): {
  valid: boolean
  error?: string
} {
  if (!key) {
    return { valid: false, error: 'API key cannot be empty' }
  }

  if (!key.startsWith('sk-')) {
    return { valid: false, error: 'API key must start with sk-' }
  }

  if (key.length < 24) {
    return {
      valid: false,
      error: 'API key is too short (minimum 24 characters)',
    }
  }

  // Check for valid characters (alphanumeric and dash)
  if (!/^sk-[a-zA-Z0-9-]+$/.test(key)) {
    return {
      valid: false,
      error: 'API key contains invalid characters',
    }
  }

  return { valid: true }
}

/**
 * User-friendly error message formatter
 */
export function formatErrorMessage(error: ApiError | Error | unknown): string {
  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const apiError = error as ApiError
    return apiError.message
  }

  return 'An unknown error occurred'
}

/**
 * Log API errors
 */
export function logApiError(
  operation: string,
  error: ApiError | Error | unknown
): void {
  if (error instanceof Error) {
    console.error(`${operation} failed: ${error.message}`, error.stack)
  } else if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error
  ) {
    const apiError = error as ApiError
    console.error(`${operation} failed: ${apiError.message}`, apiError.code)
  } else {
    console.error(`${operation} failed: ${String(error)}`)
  }
}

// Track if we're already handling a sign out to prevent multiple concurrent attempts
let isSigningOut = false

/**
 * Handle 401 Unauthorized errors
 * Signs the user out and logs the error
 *
 * @param response - The failed HTTP response
 * @param operation - Description of the operation that failed (for logging)
 */
export async function handleUnauthorized(
  response: Response,
  operation: string
): Promise<void> {
  const error = await parseApiError(response)
  logApiError(`${operation} - Unauthorized`, error)

  // Prevent multiple concurrent sign out attempts
  if (isSigningOut) {
    return
  }

  isSigningOut = true

  try {
    // Try normal sign out first
    await signOut()
    console.log('User signed out due to 401 Unauthorized error')
    isSigningOut = false
  } catch {
    // If sign out fails (e.g., session already invalid), force sign out
    // This happens when the session is already expired/invalid
    console.log('Normal sign out failed, forcing sign out...')
    forceSignOut()
    // forceSignOut will reload the page, so we won't reach here
  }
}
